type WebAuthMetricKind =
  | 'http-request'
  | 'bootstrap'
  | 'login'
  | 'select-company'
  | 'select-identity'
  | 'menu-ready';

export interface WebAuthMetric {
  kind: WebAuthMetricKind;
  at: string;
  durationMs?: number;
  method?: string;
  path?: string;
  status?: number;
  success?: boolean;
  note?: string;
}

interface WebAuthMetricStore {
  metrics: WebAuthMetric[];
  requestCount: number;
}

const MAX_METRICS = 300;

export function recordWebAuthMetric(metric: WebAuthMetric): void {
  const store = getStore();
  if (!store) {
    return;
  }

  store.metrics.push(metric);
  if (metric.kind === 'http-request') {
    store.requestCount += 1;
  }

  if (store.metrics.length > MAX_METRICS) {
    store.metrics.splice(0, store.metrics.length - MAX_METRICS);
  }

  if (import.meta.env.DEV) {
    console.info('[web-auth-metrics]', metric);
  }
}

export function startWebAuthTimer(
  kind: Exclude<WebAuthMetricKind, 'http-request'>,
  baseMetric?: Omit<WebAuthMetric, 'kind' | 'at' | 'durationMs'>,
): (result?: Omit<WebAuthMetric, 'kind' | 'at' | 'durationMs'>) => void {
  const start = nowMs();
  return (result): void => {
    recordWebAuthMetric({
      kind,
      at: new Date().toISOString(),
      durationMs: roundDuration(nowMs() - start),
      ...baseMetric,
      ...result,
    });
  };
}

function getStore(): WebAuthMetricStore | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!window.__crvWebAuthMetrics) {
    window.__crvWebAuthMetrics = {
      metrics: [],
      requestCount: 0,
    };
  }

  return window.__crvWebAuthMetrics as WebAuthMetricStore;
}

function nowMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
}

function roundDuration(value: number): number {
  return Math.round(value * 100) / 100;
}
